import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  List, 
  Typography, 
  Divider, 
  IconButton, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  useMediaQuery,
  useTheme,
  Button,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  ChevronLeft as ChevronLeftIcon,
  PlusCircle,
  BarChart3,
  Calendar,
  History,
  LogOut,
  Wallet,
  TrendingUp,
  ArrowRight,
  Grid as GridIcon
} from 'lucide-react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { styled } from '@mui/material/styles';
import TournamentForm from './TournamentForm';
import TournamentList from './TournamentList';
import BankrollChart from './BankrollChart';
import TournamentCalendar from './TournamentCalendar';
import { Logo } from './Logo';
import { collection, query, orderBy, limit, getDocs, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { sampleEvents } from '../data/calendarEvents';
import { sampleTournaments } from '../data/tournamentHistory';
import PreflopCharts from './PreflopCharts';
import HandTrainer from './HandTrainer';
import CasinoIcon from '@mui/icons-material/Casino';

// Create new components for each section with consistent spacing
const AddEntry = () => (
  <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
      Add Poker Session
    </Typography>
    <TournamentForm />
  </Box>
);

const BankrollStats = () => (
  <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Typography variant="h4" sx={{ mb: 2 }}>Bankroll Statistics</Typography>
    <BankrollChart />
  </Box>
);

const PokerCalendar = () => (
  <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Typography variant="h4" sx={{ mb: 2 }}>Live Poker Calendar</Typography>
    <TournamentCalendar />
  </Box>
);

const TournamentHistory = () => (
  <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Typography variant="h4" sx={{ mb: 2 }}>Tournament History</Typography>
    <TournamentList />
  </Box>
);

// Dashboard overview that shows all components
const DashboardOverview = () => {
  const { userData, currentUser } = useAuth();
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({
    gamesPlayed: 0,
    totalSpent: 0,
    totalProfit: 0
  });

  useEffect(() => {
    // Get the first two upcoming events from the calendar
    const today = new Date();
    const calendarEvents = sampleEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 2)
    .map(event => ({
      id: event.id,
      title: event.title,
      date: new Date(event.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      location: event.location
    }));

    setUpcomingEvents(calendarEvents);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    try {
      const q = query(
        collection(db, 'tournaments'),
        where('userId', '==', currentUser.uid),
        orderBy('date', 'desc'),
        limit(3)  // Only get the 3 most recent tournaments
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const tournamentsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tournamentsData.push({
            id: doc.id,
            name: data.tournament || data.tournamentName || 'Unnamed Tournament',
            date: data.date,
            totalSpent: (data.buyIn || 0) + (data.rebuys || 0) + (data.addOns || 0),
            result: data.profit
          });
          console.log('Tournament Data:', data);
        });
        
        setRecentTournaments(tournamentsData);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching recent tournaments:", error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      
      // Format dates as YYYY-MM-DD strings for Firestore
      const firstDayStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;

      console.log('Querying for dates between:', firstDayStr, 'and', lastDayStr);

      const q = query(
        collection(db, 'tournaments'),
        where('userId', '==', currentUser.uid),
        where('date', '>=', firstDayStr),
        where('date', '<=', lastDayStr),
        orderBy('date', 'desc')
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        let stats = {
          gamesPlayed: 0,
          totalSpent: 0,
          totalProfit: 0
        };

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Add debug logging
          console.log('Tournament found:', {
            date: data.date,
            name: data.tournament,
            isInRange: data.date >= firstDayStr && data.date <= lastDayStr
          });
          
          stats.gamesPlayed += 1;
          stats.totalSpent += (data.buyIn || 0) + (data.rebuys || 0) + (data.addOns || 0);
          stats.totalProfit += data.profit || 0;
        });

        setMonthlyStats(stats);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
    }
  }, [currentUser]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#0f172a' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>Player Dashboard</Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {userData?.username || 'User'}! Here's your poker performance at a glance.
        </Typography>
      </Box>
      
      {/* Quick Actions */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '8px' }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Quick Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            startIcon={<PlusCircle size={18} />}
            onClick={() => navigate('/dashboard/add')}
            sx={{ bgcolor: 'primary.main' }}
          >
            ADD TOURNAMENT
          </Button>
          <Button 
            variant="contained"
            startIcon={<Calendar size={18} />}
            onClick={() => navigate('/dashboard/calendar')}
            sx={{ bgcolor: 'primary.main' }}
          >
            VIEW CALENDAR
          </Button>
          <Button 
            variant="contained"
            startIcon={<BarChart3 size={18} />}
            onClick={() => navigate('/dashboard/stats')}
            sx={{ bgcolor: 'primary.main' }}
          >
            VIEW STATS
          </Button>
        </Box>
      </Paper>
      
      {/* Quick Stats */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '8px' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Quick Stats - {new Date().toLocaleString('default', { month: 'long' })} {new Date().getFullYear()}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {monthlyStats.gamesPlayed}
              </Typography>
              <Typography color="text.secondary">
                Games Played
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                R{monthlyStats.totalSpent.toLocaleString()}
              </Typography>
              <Typography color="text.secondary">
                Total Spent
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                R{monthlyStats.gamesPlayed ? Math.round(monthlyStats.totalSpent / monthlyStats.gamesPlayed).toLocaleString() : 0}
              </Typography>
              <Typography color="text.secondary">
                Average Spend
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: '8px' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 1,
                  color: monthlyStats.totalProfit >= 0 ? 'success.main' : 'error.main'
                }}
              >
                {monthlyStats.totalProfit >= 0 ? '+' : '-'}R{Math.abs(monthlyStats.totalProfit).toLocaleString()}
              </Typography>
              <Typography color="text.secondary">
                Total Profit/Loss
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Recent Tournaments and Upcoming Events */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">Recent Tournaments</Typography>
              <Button 
                size="small" 
                endIcon={<ArrowRight size={16} />}
                onClick={() => navigate('/dashboard/history')}
              >
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tournament</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Total Spent</TableCell>
                    <TableCell align="right">Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTournaments.map((tournament) => (
                    <TableRow key={tournament.id}>
                      <TableCell>{tournament.name}</TableCell>
                      <TableCell>{new Date(tournament.date).toLocaleDateString()}</TableCell>
                      <TableCell>R{tournament.totalSpent.toLocaleString()}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          color: tournament.result >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {tournament.result >= 0 ? `+R${tournament.result.toLocaleString()}` : `-R${Math.abs(tournament.result).toLocaleString()}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* Upcoming Events */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">Upcoming Events</Typography>
              <Button 
                size="small" 
                endIcon={<ArrowRight size={16} />}
                onClick={() => navigate('/dashboard/calendar')}
              >
                View Calendar
              </Button>
            </Box>
            <List sx={{ py: 0 }}>
              {upcomingEvents.map((event) => (
                <ListItem key={event.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton sx={{ borderRadius: '8px', bgcolor: 'background.default', py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Calendar size={20} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={event.title} 
                      secondary={
                        <React.Fragment>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {event.date} • {event.location}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(0),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

function Dashboard() {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const menuItems = [
    { text: 'Player Dashboard', icon: <TrendingUp size={20} />, path: '/dashboard' },
    { text: 'Add Poker Session', icon: <PlusCircle size={20} />, path: '/dashboard/add' },
    { text: 'Bankroll Stats', icon: <BarChart3 size={20} />, path: '/dashboard/stats' },
    { text: 'Live Poker Calendar', icon: <Calendar size={20} />, path: '/dashboard/calendar' },
    { text: 'Tournament History', icon: <History size={20} />, path: '/dashboard/history' },
    { text: 'Preflop Charts', icon: <GridIcon size={20} />, path: '/dashboard/preflop-charts' },
    { text: 'Hand Trainer', icon: <CasinoIcon />, path: '/dashboard/hand-trainer' },
  ];

  const drawer = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
        <Logo className="h-8 w-8" />
        <Typography variant="h6" sx={{ ml: 1 }}>Stack Track</Typography>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle} 
            sx={{ ml: 'auto' }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      
      <Divider />
      
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            {userData?.username?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ ml: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {userData?.username || 'User'}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider />
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => {
                navigate(item.path);
                if (isMobile) handleDrawerToggle();
              }}
              selected={window.location.pathname === item.path}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { xs: 'block', md: 'none' }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Stack Track
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogOut size={18} />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: '#1a2234' // Slightly lighter than the main background
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Desktop drawer - always visible */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            backgroundColor: '#1a2234' // Slightly lighter than the main background
          },
        }}
        open
      >
        <Toolbar sx={{ display: { xs: 'none', md: 'block' } }} />
        {drawer}
      </Drawer>
      
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: 0 }
        }}
      >
        <Toolbar sx={{ display: { xs: 'block', md: 'none' } }} />
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="/add" element={<AddEntry />} />
          <Route path="/stats" element={<BankrollStats />} />
          <Route path="/calendar" element={<PokerCalendar />} />
          <Route path="/history" element={<TournamentHistory />} />
          <Route path="/preflop-charts" element={<PreflopCharts />} />
          <Route path="/hand-trainer" element={<HandTrainer />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default Dashboard; 